/**
 * Core Authentication API
 * 
 * Generic authentication API endpoints that work with any tenant.
 * Uses getGlobalConfig() for API URL instead of importing from tenants.
 * 
 * This API instance is created lazily when first accessed, after ConfigProvider
 * has initialized the global config.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQuery, FetchBaseQueryError } from 'src/core/api/index';
import { checkIfStatusIsForbidden } from 'src/core/features/auth/authRedirect';
import { getGlobalConfig } from 'src/core/config';

// Type for the auth API instance - we'll infer it from the created API
type AuthApiInstance = ReturnType<typeof createAuthApiInternal>;

let authApiInstance: AuthApiInstance | null = null;

/**
 * Internal function to create the auth API instance.
 * Reads API URL from global config (set by ConfigProvider).
 */
function createAuthApiInternal() {
  const config = getGlobalConfig();
  const apiUrl = config.api.url;

  return createApi({
    reducerPath: 'authApi',
    baseQuery: createBaseQuery(
      apiUrl,
      (state: unknown) => {
        // Access auth state - type will be resolved at runtime
        const rootState = state as { auth?: { token?: string | null } };
        return rootState.auth?.token || null;
      },
      (state: unknown) => {
        // Access auth state - type will be resolved at runtime
        const rootState = state as { auth?: { username?: string | null } };
        return rootState.auth?.username || null;
      },
      (error: FetchBaseQueryError) => {
        // Only check for forbidden status if it's not a password endpoint
        const url = (error as any).meta?.request?.url || '';
        if (!url.includes('UserSecurity/password')) {
          checkIfStatusIsForbidden(error);
        }
      }
    ),
    endpoints: (build) => ({
      loginEspoCrm: build.mutation<GetUserInfoApiResponse, GetUserInfoApiArg>({
        query: (queryArg) => {
          // If baseUrl already includes /api/v1, use endpoint directly
          // Otherwise, add the prefix
          const baseUrl = config.api.url;
          const url = baseUrl.includes('/api/v1')
            ? `App/user`
            : `api/v1/App/user`;
          
          return {
            url: url,
            method: 'GET',
            headers: {
              'authorization': `Basic ${btoa(queryArg.body.username + ':' + queryArg.body.password)}`,
              'content-type': 'application/json',
            }
          };
        }
      }),
      createUser: build.mutation<CreateUserApiResponse, CreateUserApiArg>({
        query: (queryArg) => ({
          url: `UserManager/action/create`,
          method: 'POST',
          headers: {
            'accept': '*/*',
            'content-type': 'application/json',
            'authorization': 'Basic dXNlcm1hbmFnZXI6SE1qPzMyODJhVUk='
          },
          body: {
            name: queryArg.name,
            company: queryArg.company,
            email: queryArg.email,
            password: queryArg.password
          }
        })
      }),
    }),
    tagTypes: []
  });
}

/**
 * Gets or creates the auth API instance.
 * This should be called after ConfigProvider has initialized the global config.
 */
export function getAuthApi(): AuthApiInstance {
  if (!authApiInstance) {
    authApiInstance = createAuthApiInternal();
  }
  return authApiInstance;
}

/**
 * Type definitions for auth API
 */
export interface GetUserInfoApiArg {
  body: {
    username: string;
    password: string;
  };
}

export interface GetUserInfoApiResponse {
  user?: {
    id: string;
    name: string;
    userName: string;
  };
}

export interface CreateUserApiArg {
  name: string;
  company: string;
  email: string;
  password: string;
}

export interface CreateUserApiResponse {
  id?: string;
  [key: string]: unknown;
}

